import { flexRender } from '@tanstack/react-table'
import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { projectTreeItemTypeOptions } from '../../../../../../../react-query/project/query/use-get-project-tree-query'
import {
  BodyTd,
  BodyTrDnD
} from '../../../../../../../components/stories/organisms/table/styled-components'
import SkeletonComponent from '../../../../../../../components/stories/atoms/skeleton'
import TableRowCell from '../../../../../../../components/stories/organisms/table/components/row/table-row-cell'
import {
  canDropItemsToFolder,
  canDropItemsToRoot,
  getActiveItem,
  getItemsToMove,
  projectMenuDropTargetTypeOptions
} from '../utils'

const getDragHandleProps = ({ attributes, isDraggable, listeners }) =>
  isDraggable
    ? {
        ...attributes,
        ...listeners
      }
    : {}

const MovingCellSkeleton = ({ orientation }) => (
  <TableRowCell orientation={orientation}>
    <SkeletonComponent
      isLoading
      skeletonShape={{
        width: '100%',
        height: '16px',
        borderRadius: '6px'
      }}
    />
  </TableRowCell>
)

const CompanyProjectsTableRow = ({
  isMoving,
  isSelected,
  row,
  rowSelection,
  rowsById
}) => {
  const isFolder = row.original.type === projectTreeItemTypeOptions.FOLDER
  const isDraggable = !!row.original.isDraggable
  const canUseDragHandle = isDraggable && !isMoving
  const { active, over } = useDndContext()
  const activeItem = getActiveItem(active, rowsById)
  const itemsToMove = getItemsToMove({ activeItem, rowSelection, rowsById })
  const isRootLevel = row.depth === 0
  const canDropSelectionToRoot = canDropItemsToRoot(itemsToMove)
  const parentFolder =
    !isFolder && row.original.parentId
      ? rowsById.get(row.original.parentId)
      : null
  const canDropInFolder =
    isFolder && canDropItemsToFolder(itemsToMove, row.original)
  const canDropInParentFolder =
    !!parentFolder && canDropItemsToFolder(itemsToMove, parentFolder)
  const canDropToRoot = !isFolder && isRootLevel && canDropSelectionToRoot
  const isFolderGroupOver =
    isFolder &&
    over?.data.current?.type === projectMenuDropTargetTypeOptions.FOLDER &&
    over.data.current.folder?.id === row.original.id

  const {
    setNodeRef: setDraggableRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useDraggable({
    id: row.id,
    data: { type: 'row', item: row.original },
    disabled: !isDraggable
  })

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `folder-${row.original.id}`,
    data: {
      type: projectMenuDropTargetTypeOptions.FOLDER,
      folder: row.original
    },
    disabled: !isFolder
  })

  const { setNodeRef: setRowDroppableRef } = useDroppable({
    id: `row-${row.original.id}`,
    data: {
      type: projectMenuDropTargetTypeOptions.ROW,
      item: row.original
    },
    disabled: !isDraggable
  })

  const { setNodeRef: setRootDroppableRef } = useDroppable({
    id: `root-row-${row.original.id}`,
    data: {
      type: projectMenuDropTargetTypeOptions.ROOT_ROW
    },
    disabled: !canDropToRoot
  })

  const { setNodeRef: setParentFolderDroppableRef } = useDroppable({
    id: `folder-child-row-${row.original.id}`,
    data: {
      type: projectMenuDropTargetTypeOptions.FOLDER,
      folder: parentFolder
    },
    disabled: !canDropInParentFolder
  })

  const setNodeRef = (node) => {
    setDraggableRef(node)
    setRowDroppableRef(node)

    if (isFolder) {
      setDroppableRef(node)
    }

    if (canDropToRoot) {
      setRootDroppableRef(node)
    }

    if (canDropInParentFolder) {
      setParentFolderDroppableRef(node)
    }
  }

  return (
    <BodyTrDnD
      ref={setNodeRef}
      transform={CSS.Translate.toString(transform)}
      transition={transition}
      isDragging={isDragging}
      isDroppable={isFolder}
      isOver={isOver || isFolderGroupOver}
      canDrop={canDropInFolder}
      data-selected={isSelected}
    >
      {row.getVisibleCells().map((cell) => {
        const isDragHandleCell = cell.column.id === 'drag'
        const dragHandleProps = isDragHandleCell
          ? getDragHandleProps({
              attributes,
              isDraggable: canUseDragHandle,
              listeners
            })
          : {}

        return (
          <BodyTd
            key={cell.id}
            {...dragHandleProps}
            style={{
              width: cell.column.columnDef.meta?.isFlexible
                ? '100%'
                : cell.column.getSize(),
              minWidth: cell.column.columnDef.meta?.minWidth,
              cursor:
                isDragHandleCell && canUseDragHandle
                  ? isDragging
                    ? 'grabbing'
                    : 'grab'
                  : undefined,
              touchAction:
                isDragHandleCell && canUseDragHandle ? 'none' : undefined
            }}
          >
            {isMoving ? (
              <MovingCellSkeleton
                orientation={cell.column.columnDef.meta?.orientation}
              />
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </BodyTd>
        )
      })}
    </BodyTrDnD>
  )
}

export default CompanyProjectsTableRow
